<template>
  <b-modal
    :visible="visible"
    hide-header
    hide-footer
    no-close-on-backdrop
    body-class="p-0"
    id="ifm-fullscreen-modal"
    @hidden="$emit('close')"
  >
    <!-- Header -->
    <div class="ifm-header">
      <div class="ifm-header-left">
        <div class="ifm-icon">
          <i class="bx bx-show-alt"></i>
        </div>
        <div>
          <h5 class="ifm-title">Preview e Campos Internos</h5>
          <span class="ifm-subtitle">Visualize a ficha e gerencie campos de controle interno</span>
        </div>
      </div>
      <button type="button" class="ifm-close" @click="$emit('close')">
        <i class="bx bx-x"></i>
      </button>
    </div>

    <!-- Toolbar -->
    <div class="ifm-toolbar">
      <div class="toolbar-item">
        <label class="toolbar-label">Formulario</label>
        <div class="toolbar-select-wrapper">
          <select v-model="selectedFormId" class="toolbar-select">
            <option :value="null">Todos (Global)</option>
            <option
              v-for="form in forms"
              :key="form.id"
              :value="form.id"
            >{{ form.name }}</option>
          </select>
          <i class="bx bx-chevron-down toolbar-select-icon"></i>
        </div>
      </div>

      <div v-if="hasCompanionFields" class="toolbar-item">
        <label class="toolbar-label">Visualizar como</label>
        <div class="toggle-group">
          <button
            type="button"
            class="toggle-btn"
            :class="{ active: perspective === 'principal' }"
            @click="perspective = 'principal'"
          >
            <i class="bx bx-user"></i>
            <span>Principal</span>
          </button>
          <button
            type="button"
            class="toggle-btn"
            :class="{ active: perspective === 'companion' }"
            @click="perspective = 'companion'"
          >
            <i class="bx bx-group"></i>
            <span>Acompanhante</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Body: split layout -->
    <div class="ifm-body">
      <!-- Left: Card view -->
      <div class="ifm-left" :class="{ 'sidebar-open': sidebarActive }">
        <!-- Empty State -->
        <div v-if="visibleTabs.length === 0 && !creatingTab" class="ifm-empty">
          <i class="bx bx-info-circle"></i>
          <p>Nenhuma aba na ficha.</p>
          <button type="button" class="btn-new-tab-empty" @click="startCreateTab">
            <i class="bx bx-plus"></i> Criar primeira aba
          </button>
        </div>

        <!-- Card Content -->
        <div v-else class="ifm-content">
          <draggable
            v-model="draggableTabs"
            handle=".ifm-drag-handle"
            animation="200"
            @end="onTabsDragEnd"
          >
          <b-card
            v-for="tab in draggableTabs"
            :key="'ifm-tab-' + tab.id"
            no-body
            class="mb-1 shadow-none"
          >
            <b-card-header header-tag="header" role="tab">
              <h6 class="m-0 d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <span class="ifm-drag-handle">
                    <i class="bx bx-grid-vertical"></i>
                  </span>
                  <a
                    :href="'#ifm-collapse-' + tab.id"
                    v-b-toggle="'ifm-accordion-' + tab.id"
                    class="text-dark"
                  >
                    {{ tab.name }}
                  </a>
                </div>
                <div class="d-flex align-items-center">
                  <span class="badge badge-soft-primary mr-2">{{ getFilteredFields(tab).length }} campos</span>
                  <button
                    type="button"
                    class="btn-add-inline ml-2"
                    title="Adicionar campo interno nesta aba"
                    @click.stop="openSidebar(tab)"
                  >
                    <i class="bx bx-plus"></i> Novo campo
                  </button>
                  <i
                    class="bx bx-chevron-down chevron-icon ml-2"
                    v-b-toggle="'ifm-accordion-' + tab.id"
                  ></i>
                </div>
              </h6>
            </b-card-header>
            <b-collapse
              :id="'ifm-accordion-' + tab.id"
              :visible="false"
              accordion="ifm-accordion"
              role="tabpanel"
            >
              <b-card-body>
                <form autocomplete="off">
                  <draggable
                    :value="getFilteredFields(tab)"
                    handle=".ifm-field-drag"
                    animation="200"
                    class="row"
                    @end="onFieldsDragEnd($event, tab)"
                  >
                    <div
                      v-for="field in getFilteredFields(tab)"
                      :key="'ifm-field-' + field.id"
                      :class="'col-sm-' + getFieldSize(field)"
                    >
                      <div
                        class="ifm-field-wrap"
                        :class="{
                          'is-internal': field.field_source === 'internal',
                          'is-editing': editingField && editingField.id === field.id
                        }"
                      >
                        <!-- Toolbar: drag + resize + actions -->
                        <div class="ifm-field-toolbar">
                          <span class="ifm-field-drag" title="Arrastar para reordenar">
                            <i class="bx bx-grid-vertical"></i>
                          </span>
                          <select
                            class="ifm-cols-select"
                            :value="getFieldSize(field)"
                            title="Largura do campo"
                            @change="updateFieldCols(tab, field, $event.target.value)"
                          >
                            <option value="12">100%</option>
                            <option value="9">75%</option>
                            <option value="8">66%</option>
                            <option value="6">50%</option>
                            <option value="4">33%</option>
                            <option value="3">25%</option>
                          </select>
                          <button
                            v-if="field.field_source === 'internal'"
                            type="button"
                            class="ifm-action-btn edit"
                            title="Editar"
                            @click="openEditSidebar(tab, field)"
                          >
                            <i class="bx bx-pencil"></i>
                          </button>
                          <button
                            v-if="field.field_source === 'internal'"
                            type="button"
                            class="ifm-action-btn delete"
                            title="Remover"
                            @click="confirmDeleteField(tab, field)"
                          >
                            <i class="bx bx-trash"></i>
                          </button>
                        </div>

                        <component
                          :is="getFieldComponent(field)"
                          :id="field.id"
                          :value="''"
                          :label="getFieldLabel(field)"
                          :placeholder="field.placeholder || ''"
                          :options="getFieldOptions(field)"
                          class-input="col-sm-12"
                          :is-required="field.is_required"
                          :is-disabled="true"
                          :is-readonly="field.is_readonly"
                        />
                      </div>
                    </div>

                  </draggable>
                  <!-- Live preview while creating (outside draggable) -->
                  <div class="row">
                    <div
                      v-if="sidebarActive && sidebarTargetTabId === tab.id && sidebarMode === 'create' && sidebarStep === 'configure'"
                      ref="livePreview"
                      :class="'col-sm-' + (sidebarForm.cols || 12)"
                    >
                      <div class="ifm-live-preview">
                        <component
                          :is="getLivePreviewComponent()"
                          :id="0"
                          :value="''"
                          :label="sidebarForm.label || 'Novo campo...'"
                          :placeholder="sidebarForm.placeholder || ''"
                          :options="getLivePreviewOptions()"
                          class-input="col-sm-12"
                          :is-disabled="true"
                        />
                      </div>
                    </div>
                    <!-- Skeleton while selecting type -->
                    <div
                      v-if="sidebarActive && sidebarTargetTabId === tab.id && sidebarMode === 'create' && sidebarStep === 'select'"
                      ref="skeletonPreview"
                      :class="'col-sm-12'"
                    >
                      <div class="ifm-skeleton-field">
                        <div class="skeleton-label"></div>
                        <div class="skeleton-input"></div>
                      </div>
                    </div>
                  </div>
                </form>
              </b-card-body>
            </b-collapse>
          </b-card>
          </draggable>

          <!-- Create New Tab -->
          <div v-if="creatingTab" class="ifm-new-tab-form">
            <div class="ifm-inline-header">
              <i class="bx bx-plus-circle"></i>
              <span>Nova aba</span>
            </div>
            <div class="d-flex gap-8">
              <input
                ref="newTabName"
                v-model="newTabName"
                type="text"
                class="form-control form-control-sm"
                placeholder="Nome da aba (ex: Controle Interno)"
                @keyup.enter="submitNewTab"
              />
              <button class="btn btn-sm btn-light ml-2" @click="cancelCreateTab">Cancelar</button>
              <button
                class="btn btn-sm btn-primary ml-2"
                :disabled="!newTabName || saving"
                @click="submitNewTab"
              >Criar</button>
            </div>
          </div>
          <button v-else type="button" class="ifm-btn-new-tab" @click="startCreateTab">
            <i class="bx bx-plus"></i> Nova aba
          </button>
        </div>
      </div>

      <!-- Right: Sidebar -->
      <transition name="sidebar-slide">
        <div v-if="sidebarActive" class="ifm-sidebar">
          <div class="sidebar-header">
            <button v-if="sidebarStep === 'configure'" type="button" class="btn-back-sidebar" @click="sidebarStep = 'select'">
              <i class="bx bx-arrow-back"></i>
            </button>
            <div class="sidebar-title">
              <i :class="sidebarMode === 'edit' ? 'bx bx-pencil' : 'bx bx-plus-circle'"></i>
              <span v-if="sidebarMode === 'edit'">Editar campo</span>
              <span v-else-if="sidebarStep === 'select'">Tipo de campo</span>
              <span v-else>
                <i :class="sidebarSelectedType ? sidebarSelectedType.icon : ''"></i>
                {{ sidebarSelectedType ? sidebarSelectedType.label : '' }}
              </span>
            </div>
            <button type="button" class="btn-close-sidebar" @click="closeSidebar">
              <i class="bx bx-x"></i>
            </button>
          </div>

          <div class="sidebar-body">
            <!-- Step 1: Select field type (create mode only) -->
            <div v-if="sidebarMode === 'create' && sidebarStep === 'select'" class="sidebar-type-list">
              <div class="sidebar-intro">Selecione o tipo de campo:</div>
              <div
                v-for="ft in fieldTypes"
                :key="ft.name"
                class="sidebar-type-item"
                @click="selectSidebarType(ft)"
              >
                <div class="sidebar-type-icon">
                  <i :class="ft.icon"></i>
                </div>
                <div class="sidebar-type-info">
                  <span class="sidebar-type-name">{{ ft.label }}</span>
                  <span class="sidebar-type-desc">{{ ft.description }}</span>
                </div>
                <i class="bx bx-chevron-right"></i>
              </div>
            </div>

            <!-- Step 2: Configure field (create & edit) -->
            <div v-if="sidebarStep === 'configure'" class="sidebar-config">
              <!-- Configuracoes Basicas -->
              <div class="settings-section">
                <div class="section-title">Configuracoes Basicas</div>

                <div class="form-group">
                  <label>Label do Campo *</label>
                  <input
                    ref="sidebarLabel"
                    v-model="sidebarForm.label"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Ja retirou kit?"
                  />
                </div>

                <div class="form-group">
                  <label>Placeholder</label>
                  <input
                    v-model="sidebarForm.placeholder"
                    type="text"
                    class="form-control"
                    placeholder="Texto de exemplo..."
                  />
                </div>

                <div class="form-group">
                  <label>Texto de Ajuda</label>
                  <input
                    v-model="sidebarForm.help_text"
                    type="text"
                    class="form-control"
                    placeholder="Instrucoes para o usuario..."
                  />
                </div>

                <!-- Options for select/radio/checkbox/multiselect -->
                <div v-if="sidebarFieldHasOptions" class="form-group">
                  <label>Opcoes</label>
                  <div class="options-list">
                    <div
                      v-for="(opt, oi) in sidebarForm.options"
                      :key="oi"
                      class="option-item"
                    >
                      <input
                        v-model="opt.label"
                        type="text"
                        class="form-control"
                        placeholder="Label"
                        @input="autoValue(opt)"
                      />
                      <button
                        v-if="sidebarForm.options.length > 1"
                        type="button"
                        class="btn-remove-option"
                        @click="sidebarForm.options.splice(oi, 1)"
                      ><i class="bx bx-x"></i></button>
                    </div>
                    <button type="button" class="btn-add-option" @click="sidebarForm.options.push({ label: '', value: '' })">
                      <i class="bx bx-plus"></i> Adicionar opcao
                    </button>
                  </div>
                </div>

                <div class="form-group toggle-group">
                  <label>Campo Obrigatorio</label>
                  <b-form-checkbox
                    v-model="sidebarForm.required"
                    switch
                  />
                </div>
              </div>

              <!-- Visibilidade -->
              <div class="settings-section">
                <div class="section-title">Visibilidade</div>

                <div class="form-group toggle-group">
                  <label>
                    Campo Visivel
                    <small class="text-muted d-block" style="font-weight: normal; margin-top: 4px;">
                      Quando desativado, o campo nao aparece na ficha
                    </small>
                  </label>
                  <b-form-checkbox
                    v-model="sidebarForm.visible"
                    switch
                  />
                </div>
              </div>

              <!-- Configuracoes Avancadas -->
              <div class="settings-section">
                <div class="section-title">Configuracoes Avancadas</div>

                <div class="form-group">
                  <label>Largura</label>
                  <select v-model="sidebarForm.cols" class="form-control">
                    <option :value="12">Largura total (100%)</option>
                    <option :value="6">Meia largura (50%)</option>
                    <option :value="4">Um terco (33%)</option>
                    <option :value="3">Um quarto (25%)</option>
                    <option :value="8">Dois tercos (66%)</option>
                    <option :value="9">Tres quartos (75%)</option>
                  </select>
                </div>

                <div class="form-group toggle-group">
                  <label>Somente Leitura</label>
                  <b-form-checkbox
                    v-model="sidebarForm.readonly"
                    switch
                  />
                </div>
              </div>

              <div class="config-actions">
                <button type="button" class="btn-cancel" @click="closeSidebar">
                  Cancelar
                </button>
                <button
                  type="button"
                  class="btn-create"
                  :disabled="!sidebarForm.label || saving"
                  @click="submitSidebar"
                >
                  <i v-if="saving" class="bx bx-loader-alt bx-spin"></i>
                  <i v-else-if="sidebarMode === 'edit'" class="bx bx-check"></i>
                  <i v-else class="bx bx-plus"></i>
                  {{ sidebarMode === 'edit' ? 'Salvar' : 'Adicionar' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </b-modal>
</template>

<script>
import http from '@/http'
import draggable from 'vuedraggable'
import BaseInput from '@/components/formSchema/BaseInput'
import BaseInputEmail from '@/components/formSchema/BaseInputEmail'
import BaseInputPhone from '@/components/formSchema/BaseInputPhone'
import BaseSelect from '@/components/formSchema/BaseSelect'
import BaseRadio from '@/components/formSchema/BaseRadio'
import BaseTextArea from '@/components/formSchema/BaseTextArea'
import BaseInputDate from '@/components/formSchema/BaseInputDate'
import BaseInputCPF from '@/components/formSchema/BaseInputCPF'
import BaseInputCNPJ from '@/components/formSchema/BaseInputCNPJ'

export default {
  name: 'InternalFieldsModal',
  components: {
    draggable: draggable,
    BaseInput: BaseInput,
    BaseInputEmail: BaseInputEmail,
    BaseInputPhone: BaseInputPhone,
    BaseSelect: BaseSelect,
    BaseRadio: BaseRadio,
    BaseTextArea: BaseTextArea,
    BaseInputDate: BaseInputDate,
    BaseInputCPF: BaseInputCPF,
    BaseInputCNPJ: BaseInputCNPJ
  },
  props: {
    visible: { type: Boolean, default: false },
    tabs: { type: Array, default: function () { return [] } },
    cardName: { type: String, default: 'Ficha do Participante' }
  },
  data: function () {
    return {
      selectedFormId: null,
      perspective: 'principal',
      forms: [],
      saving: false,
      creatingTab: false,
      newTabName: '',
      // Sidebar state
      sidebarActive: false,
      sidebarMode: 'create', // 'create' or 'edit'
      sidebarStep: 'select', // 'select' or 'configure'
      sidebarTargetTabId: null,
      sidebarSelectedType: null,
      sidebarForm: {
        label: '',
        placeholder: '',
        help_text: '',
        cols: 12,
        required: false,
        visible: true,
        readonly: false,
        options: [{ label: '', value: '' }]
      },
      editingField: null,
      fieldTypes: [
        { name: 'text', label: 'Texto', icon: 'bx bx-text', description: 'Campo de texto simples' },
        { name: 'textarea', label: 'Area de Texto', icon: 'bx bx-align-left', description: 'Texto multilinha' },
        { name: 'number', label: 'Numero', icon: 'bx bx-hash', description: 'Apenas numeros' },
        { name: 'select', label: 'Lista Suspensa', icon: 'bx bx-chevron-down', description: 'Selecao unica' },
        { name: 'multiselect', label: 'Selecao Multipla', icon: 'bx bx-list-check', description: 'Varias opcoes' },
        { name: 'checkbox', label: 'Checkbox', icon: 'bx bx-checkbox-checked', description: 'Caixa de marcacao' },
        { name: 'radio', label: 'Radio', icon: 'bx bx-radio-circle-marked', description: 'Escolha unica' },
        { name: 'date', label: 'Data', icon: 'bx bx-calendar', description: 'Seletor de data' },
        { name: 'datetime', label: 'Data e Hora', icon: 'bx bx-calendar-event', description: 'Data com hora' },
        { name: 'toggle', label: 'Toggle', icon: 'bx bx-toggle-right', description: 'Liga/Desliga' }
      ],
      states: [
        { value: 'AC', label: 'Acre' },
        { value: 'AL', label: 'Alagoas' },
        { value: 'AP', label: 'Amapa' },
        { value: 'AM', label: 'Amazonas' },
        { value: 'BA', label: 'Bahia' },
        { value: 'CE', label: 'Ceara' },
        { value: 'DF', label: 'Distrito Federal' },
        { value: 'ES', label: 'Espirito Santo' },
        { value: 'GO', label: 'Goias' },
        { value: 'MA', label: 'Maranhao' },
        { value: 'MT', label: 'Mato Grosso' },
        { value: 'MS', label: 'Mato Grosso do Sul' },
        { value: 'MG', label: 'Minas Gerais' },
        { value: 'PA', label: 'Para' },
        { value: 'PB', label: 'Paraiba' },
        { value: 'PR', label: 'Parana' },
        { value: 'PE', label: 'Pernambuco' },
        { value: 'PI', label: 'Piaui' },
        { value: 'RJ', label: 'Rio de Janeiro' },
        { value: 'RN', label: 'Rio Grande do Norte' },
        { value: 'RS', label: 'Rio Grande do Sul' },
        { value: 'RO', label: 'Rondonia' },
        { value: 'RR', label: 'Roraima' },
        { value: 'SC', label: 'Santa Catarina' },
        { value: 'SP', label: 'Sao Paulo' },
        { value: 'SE', label: 'Sergipe' },
        { value: 'TO', label: 'Tocantins' }
      ]
    }
  },
  computed: {
    hasCompanionFields: function () {
      if (!this.tabs || !Array.isArray(this.tabs)) return false
      for (var i = 0; i < this.tabs.length; i++) {
        var tab = this.tabs[i]
        if (!tab.fields) continue
        for (var j = 0; j < tab.fields.length; j++) {
          var fc = tab.fields[j].field_config
          if (fc && fc.display_for) return true
        }
      }
      return false
    },
    visibleTabs: function () {
      if (!this.tabs || !Array.isArray(this.tabs)) return []
      return this.tabs.slice().sort(function (a, b) {
        return (a.order || 0) - (b.order || 0)
      })
    },
    draggableTabs: {
      get: function () {
        return this.visibleTabs
      },
      set: function () {
        // handled by onTabsDragEnd
      }
    },
    sidebarFieldHasOptions: function () {
      if (!this.sidebarSelectedType) return false
      return ['select', 'multiselect', 'radio', 'checkbox'].indexOf(this.sidebarSelectedType.name) !== -1
    }
  },
  watch: {
    visible: function (val) {
      if (val) {
        this.loadForms()
        this.closeSidebar()
      }
    }
  },
  methods: {
    // ---- Field rendering (same as CardPreviewModal) ----
    getFieldComponent: function (field) {
      if (field.field_source === 'standard') {
        var map = {
          name: 'BaseInput',
          email: 'BaseInputEmail',
          cpf: 'BaseInputCPF',
          phone: 'BaseInputPhone',
          gender: 'BaseSelect',
          birth_date: 'BaseInputDate',
          address: 'BaseInput',
          city: 'BaseInput',
          state: 'BaseSelect',
          zip_code: 'BaseInput',
          country: 'BaseInput'
        }
        return map[field.standard_field_name] || 'BaseInput'
      }
      if (field.field_source === 'form_field' && field.form_field && field.form_field.field_type) {
        return this.mapComponent(field.form_field.field_type.component || 'TextField')
      }
      if (field.field_source === 'internal' && field.field_config) {
        var imap = {
          text: 'BaseInput',
          textarea: 'BaseTextArea',
          number: 'BaseInput',
          select: 'BaseSelect',
          multiselect: 'BaseSelect',
          checkbox: 'BaseRadio',
          radio: 'BaseRadio',
          date: 'BaseInputDate',
          datetime: 'BaseInputDate',
          toggle: 'BaseRadio'
        }
        return imap[field.field_config.field_type] || 'BaseInput'
      }
      return 'BaseInput'
    },
    mapComponent: function (name) {
      var m = {
        'EmailField': 'BaseInputEmail',
        'TextField': 'BaseInput',
        'PhoneField': 'BaseInputPhone',
        'SelectField': 'BaseSelect',
        'RadioField': 'BaseRadio',
        'TextAreaField': 'BaseTextArea',
        'DateField': 'BaseInputDate',
        'CPFField': 'BaseInputCPF',
        'CNPJField': 'BaseInputCNPJ'
      }
      return m[name] || 'BaseInput'
    },
    getFieldOptions: function (field) {
      if (field.field_source === 'standard') {
        if (field.standard_field_name === 'gender') {
          return [
            { value: 'M', label: 'Masculino' },
            { value: 'F', label: 'Feminino' },
            { value: 'O', label: 'Outro' }
          ]
        }
        if (field.standard_field_name === 'state') return this.states
      }
      if (field.field_source === 'form_field' && field.form_field) return field.form_field.options || []
      if (field.field_source === 'internal' && field.field_config && field.field_config.options) return field.field_config.options
      return []
    },
    getFieldLabel: function (field) {
      if (field.field_config && field.field_config.label_override) return field.field_config.label_override
      if (field.label) return field.label
      if (field.field_config && field.field_config.label) return field.field_config.label
      if (field.field_source === 'standard') {
        var labels = {
          name: 'Nome Completo',
          badge_name: 'Nome para Cracha',
          email: 'E-mail',
          cpf: 'CPF',
          phone: 'Telefone',
          gender: 'Genero',
          birth_date: 'Data de Nascimento',
          address: 'Endereco',
          city: 'Cidade',
          state: 'Estado',
          zip_code: 'CEP',
          country: 'Pais'
        }
        return labels[field.standard_field_name] || field.standard_field_name
      }
      if (field.field_source === 'form_field' && field.form_field) return field.form_field.label || field.form_field.name
      if (field.field_source === 'internal' && field.field_config) return field.field_config.label || field.field_config.name
      return 'Campo'
    },
    getFieldSize: function (field) {
      return (field.layout && field.layout.cols) ? field.layout.cols : 12
    },
    getFilteredFields: function (tab) {
      var self = this
      if (!tab || !tab.fields) return []
      return tab.fields.slice().sort(function (a, b) {
        return (a.order || 0) - (b.order || 0)
      }).filter(function (field) {
        if (self.selectedFormId) {
          if (field.field_source === 'form_field' && field.form_field) {
            if (field.form_field.form_id !== self.selectedFormId) return false
          }
        }
        if (self.hasCompanionFields) {
          var displayFor = field.field_config ? field.field_config.display_for : null
          if (displayFor) {
            if (self.perspective === 'principal' && displayFor === 'companion') return false
            if (self.perspective === 'companion' && displayFor === 'principal') return false
          }
        }
        return true
      })
    },
    loadForms: function () {
      var self = this
      http.get('/admin/forms', { params: { all: true } })
        .then(function (r) { self.forms = r.data.data || r.data || [] })
        .catch(function () { self.forms = [] })
    },

    // ---- Sidebar ----
    openSidebar: function (tab) {
      this.editingField = null
      this.sidebarMode = 'create'
      this.sidebarStep = 'select'
      this.sidebarTargetTabId = tab.id
      this.sidebarSelectedType = null
      this.sidebarForm = {
        label: '',
        placeholder: '',
        help_text: '',
        cols: 12,
        required: false,
        visible: true,
        readonly: false,
        options: [{ label: '', value: '' }]
      }
      this.sidebarActive = true
      this.scrollToPreview('skeletonPreview')
    },
    openEditSidebar: function (tab, field) {
      var fc = field.field_config || {}
      var ft = fc.field_type || 'text'
      this.editingField = field
      this.sidebarMode = 'edit'
      this.sidebarStep = 'configure'
      this.sidebarTargetTabId = tab.id
      this.sidebarSelectedType = this.fieldTypes.find(function (t) { return t.name === ft }) || this.fieldTypes[0]
      this.sidebarForm = {
        label: fc.label || field.label || '',
        placeholder: fc.placeholder || field.placeholder || '',
        help_text: fc.help_text || '',
        cols: (field.layout && field.layout.cols) || 12,
        required: field.is_required || false,
        visible: field.is_visible !== false,
        readonly: field.is_readonly || false,
        options: fc.options ? JSON.parse(JSON.stringify(fc.options)) : [{ label: '', value: '' }]
      }
      this.sidebarActive = true
    },
    selectSidebarType: function (ft) {
      this.sidebarSelectedType = ft
      this.sidebarStep = 'configure'
      var self = this
      this.$nextTick(function () {
        if (self.$refs.sidebarLabel) {
          var el = Array.isArray(self.$refs.sidebarLabel) ? self.$refs.sidebarLabel[0] : self.$refs.sidebarLabel
          if (el) el.focus()
        }
        self.scrollToPreview('livePreview')
      })
    },
    closeSidebar: function () {
      this.sidebarActive = false
      this.sidebarMode = 'create'
      this.sidebarStep = 'select'
      this.sidebarTargetTabId = null
      this.sidebarSelectedType = null
      this.editingField = null
    },
    submitSidebar: function () {
      if (!this.sidebarForm.label) return
      if (this.sidebarMode === 'edit') {
        this.saveEditField()
      } else {
        this.submitNewField()
      }
    },
    submitNewField: function () {
      var sf = this.sidebarForm
      var ft = this.sidebarSelectedType
      if (!sf.label || !ft) return
      var options = null
      if (this.sidebarFieldHasOptions) {
        options = sf.options.filter(function (o) { return o.label.trim() }).map(function (o) {
          return {
            label: o.label,
            value: o.value || slugify(o.label)
          }
        })
        if (options.length === 0) options = [{ label: 'Opcao 1', value: 'opcao_1' }]
      }
      this.saving = true
      var self = this
      this.$emit('create-field', {
        tabId: this.sidebarTargetTabId,
        data: {
          field_source: 'internal',
          label: sf.label,
          layout: { cols: sf.cols },
          is_required: sf.required,
          is_visible: sf.visible,
          is_readonly: sf.readonly,
          field_config: {
            field_type: ft.name,
            label: sf.label,
            name: slugify(sf.label),
            placeholder: sf.placeholder,
            help_text: sf.help_text,
            options: options,
            is_internal: true
          }
        },
        callback: function (success) {
          self.saving = false
          if (success) self.closeSidebar()
        }
      })
    },
    saveEditField: function () {
      var sf = this.sidebarForm
      var ft = this.sidebarSelectedType
      if (!sf.label || !this.editingField) return
      var options = null
      if (this.sidebarFieldHasOptions) {
        options = sf.options.filter(function (o) { return o.label.trim() }).map(function (o) {
          return {
            label: o.label,
            value: o.value || slugify(o.label)
          }
        })
      }
      this.saving = true
      var self = this
      this.$emit('update-field', {
        tabId: this.sidebarTargetTabId,
        fieldId: this.editingField.id,
        data: {
          field_source: 'internal',
          label: sf.label,
          placeholder: sf.placeholder,
          layout: { cols: sf.cols },
          is_required: sf.required,
          is_visible: sf.visible,
          is_readonly: sf.readonly,
          field_config: {
            field_type: ft.name,
            label: sf.label,
            name: slugify(sf.label),
            placeholder: sf.placeholder,
            help_text: sf.help_text,
            options: options,
            is_internal: true
          }
        },
        callback: function (success) {
          self.saving = false
          if (success) self.closeSidebar()
        }
      })
    },

    // ---- Delete ----
    confirmDeleteField: function (tab, field) {
      var label = (field.field_config && field.field_config.label) || field.label || 'este campo'
      var self = this
      this.$bvModal.msgBoxConfirm(
        'Remover o campo "' + label + '"?',
        {
          title: 'Remover Campo',
          okTitle: 'Remover',
          okVariant: 'danger',
          cancelTitle: 'Cancelar',
          centered: true
        }
      ).then(function (ok) {
        if (ok) {
          self.$emit('delete-field', {
            tabId: tab.id,
            fieldId: field.id
          })
        }
      })
    },

    // ---- Reorder Tabs (drag & drop) ----
    onTabsDragEnd: function (evt) {
      var oldIndex = evt.oldIndex
      var newIndex = evt.newIndex
      if (oldIndex === newIndex) return
      var ordered = this.visibleTabs.slice()
      var moved = ordered.splice(oldIndex, 1)[0]
      ordered.splice(newIndex, 0, moved)
      var reordered = ordered.map(function (tab, idx) {
        return {
          id: tab.id,
          order: idx
        }
      })
      this.$emit('reorder-tabs', {
        tabs: reordered,
        callback: function () {}
      })
    },

    // ---- Reorder Fields (drag & drop) ----
    onFieldsDragEnd: function (evt, tab) {
      var oldIndex = evt.oldIndex
      var newIndex = evt.newIndex
      if (oldIndex === newIndex) return
      var fields = this.getFilteredFields(tab)
      var ordered = fields.slice()
      var moved = ordered.splice(oldIndex, 1)[0]
      ordered.splice(newIndex, 0, moved)
      this.$emit('reorder-fields', {
        tabId: tab.id,
        fields: ordered
      })
    },

    // ---- Resize Field ----
    updateFieldCols: function (tab, field, cols) {
      var newCols = parseInt(cols)
      if (isNaN(newCols)) return
      var data = {
        field_source: field.field_source,
        layout: { cols: newCols }
      }
      if (field.field_source === 'standard') {
        data.standard_field_name = field.standard_field_name
      }
      this.$emit('update-field', {
        tabId: tab.id,
        fieldId: field.id,
        data: data,
        callback: function () {}
      })
    },

    // ---- Live Preview ----
    getLivePreviewComponent: function () {
      if (!this.sidebarSelectedType) return 'BaseInput'
      var imap = {
        text: 'BaseInput',
        textarea: 'BaseTextArea',
        number: 'BaseInput',
        select: 'BaseSelect',
        multiselect: 'BaseSelect',
        checkbox: 'BaseRadio',
        radio: 'BaseRadio',
        date: 'BaseInputDate',
        datetime: 'BaseInputDate',
        toggle: 'BaseRadio'
      }
      return imap[this.sidebarSelectedType.name] || 'BaseInput'
    },
    getLivePreviewOptions: function () {
      if (!this.sidebarFieldHasOptions) return []
      return this.sidebarForm.options.filter(function (o) { return o.label.trim() }).map(function (o) {
        return {
          label: o.label,
          value: o.value || o.label
        }
      })
    },

    // ---- New Tab ----
    startCreateTab: function () {
      this.creatingTab = true
      this.newTabName = ''
      var self = this
      this.$nextTick(function () {
        if (self.$refs.newTabName) {
          var el = Array.isArray(self.$refs.newTabName) ? self.$refs.newTabName[0] : self.$refs.newTabName
          if (el) el.focus()
        }
      })
    },
    submitNewTab: function () {
      if (!this.newTabName) return
      this.saving = true
      var self = this
      this.$emit('create-tab', {
        name: this.newTabName,
        callback: function (success) {
          self.saving = false
          if (success) {
            self.creatingTab = false
            self.newTabName = ''
          }
        }
      })
    },
    cancelCreateTab: function () {
      this.creatingTab = false
      this.newTabName = ''
    },

    // ---- Helpers ----
    autoValue: function (opt) {
      if (opt.label && !opt.value) opt.value = slugify(opt.label)
    },
    scrollToPreview: function (refName) {
      var self = this
      this.$nextTick(function () {
        var el = self.$refs[refName]
        if (Array.isArray(el)) el = el[0]
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
      })
    }
  }
}

function slugify (text) {
  if (!text) return ''
  return text.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
}
</script>

<style scoped>
/* Fullwidth modal */
::v-deep #ifm-fullscreen-modal > .modal-dialog {
  max-width: 100%;
  height: 100%;
  margin: 0;
}
::v-deep #ifm-fullscreen-modal > .modal-dialog > .modal-content {
  height: 100%;
  border-radius: 0;
  display: flex;
  flex-direction: column;
}
::v-deep #ifm-fullscreen-modal > .modal-dialog > .modal-content > .modal-body {
  flex: 1;
  overflow: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
}

/* Header */
.ifm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}
.ifm-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.ifm-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981, #34d399);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  flex-shrink: 0;
}
.ifm-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
  line-height: 1.3;
}
.ifm-subtitle {
  font-size: 12px;
  color: #8c8ca1;
}
.ifm-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #8c8ca1;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
}
.ifm-close:hover {
  background: #f5f5f5;
  color: #1a1a2e;
}

/* Toolbar */
.ifm-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  padding: 16px 24px;
  background: #fafbfc;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}
.toolbar-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.toolbar-item:first-child {
  flex: 1;
  min-width: 0;
}
.toolbar-label {
  font-size: 11px;
  font-weight: 600;
  color: #8c8ca1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}
.toolbar-select-wrapper {
  position: relative;
}
.toolbar-select {
  appearance: none;
  width: 100%;
  padding: 8px 32px 8px 12px;
  font-size: 13px;
  color: #1a1a2e;
  background: #fff;
  border: 1px solid #e2e5ea;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s;
}
.toolbar-select:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}
.toolbar-select-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #8c8ca1;
  pointer-events: none;
}

/* Toggle */
.toggle-group {
  display: flex;
  background: #e9ecef;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}
.toggle-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.toggle-btn i { font-size: 15px; }
.toggle-btn:hover:not(.active) {
  color: #495057;
  background: rgba(255,255,255,0.5);
}
.toggle-btn.active {
  background: #fff;
  color: #10b981;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

/* Body: split layout */
.ifm-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.ifm-left {
  flex: 1;
  overflow-y: auto;
  transition: flex 0.3s ease;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}
.ifm-left::-webkit-scrollbar {
  width: 6px;
}
.ifm-left::-webkit-scrollbar-track {
  background: transparent;
}
.ifm-left::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}
.ifm-left::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}
.ifm-left.sidebar-open {
  flex: 1;
}

/* Empty */
.ifm-empty {
  text-align: center;
  color: #8c8ca1;
  padding: 60px 24px;
}
.ifm-empty i { font-size: 48px; }
.ifm-empty p { margin-top: 12px; font-size: 14px; }
.btn-new-tab-empty {
  margin-top: 12px;
  padding: 8px 20px;
  border: 1px dashed #10b981;
  background: transparent;
  color: #10b981;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-new-tab-empty:hover {
  background: #ecfdf5;
}

/* Content */
.ifm-content {
  padding: 16px 24px 24px;
}

/* Add field button in accordion header */
.btn-add-inline {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #10b981;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.btn-add-inline:hover {
  background: #d1fae5;
  border-color: #6ee7b7;
}
.btn-add-inline i {
  font-size: 14px;
}

/* Drag handle */
.ifm-drag-handle {
  cursor: grab;
  color: #adb5bd;
  margin-right: 8px;
  font-size: 18px;
  display: flex;
  align-items: center;
}
.ifm-drag-handle:hover {
  color: #6c757d;
}
.ifm-drag-handle:active {
  cursor: grabbing;
}

/* Chevron */
.chevron-icon {
  font-size: 18px;
  transition: transform 0.2s ease;
  color: #6c757d;
  cursor: pointer;
}
.chevron-icon.collapsed {
  transform: rotate(-90deg);
}

/* Field wrapper */
.ifm-field-wrap {
  position: relative;
  transition: all 0.15s;
  border-radius: 6px;
  padding: 2px;
  border: 1px dashed transparent;
}
.ifm-field-wrap:hover {
  border-color: #d0d5dd;
  background: #fafbfc;
}
.ifm-field-wrap.is-internal:hover {
  border-color: #10b981;
  background: #f0fdf4;
}
.ifm-field-wrap.is-editing {
  border-color: #10b981;
  background: #ecfdf5;
}

/* Field toolbar (drag + resize + actions) */
.ifm-field-toolbar {
  position: absolute;
  top: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 2;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  padding: 1px 2px;
}
.ifm-field-wrap:hover .ifm-field-toolbar {
  opacity: 1;
}

/* Drag handle */
.ifm-field-drag {
  cursor: grab;
  color: #adb5bd;
  font-size: 16px;
  display: flex;
  align-items: center;
  padding: 2px;
}
.ifm-field-drag:hover {
  color: #6c757d;
}
.ifm-field-drag:active {
  cursor: grabbing;
}

/* Cols select */
.ifm-cols-select {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  background: #f3f4f6;
  color: #495057;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 4px;
  border-radius: 3px;
  cursor: pointer;
  width: 46px;
  text-align: center;
}
.ifm-cols-select:hover {
  background: #e5e7eb;
}
.ifm-cols-select:focus {
  outline: none;
  background: #dbeafe;
  color: #2563eb;
}

/* Action buttons */
.ifm-action-btn {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s;
}
.ifm-action-btn.edit {
  background: #dbeafe;
  color: #2563eb;
}
.ifm-action-btn.edit:hover {
  background: #bfdbfe;
}
.ifm-action-btn.delete {
  background: #fee2e2;
  color: #dc2626;
}
.ifm-action-btn.delete:hover {
  background: #fecaca;
}

/* Draggable ghost/drag state */
.ifm-field-wrap.sortable-ghost {
  opacity: 0.4;
  border-color: #556ee6;
  background: #eff1fb;
}
.ifm-field-wrap.sortable-chosen {
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

/* Live preview field */
.ifm-live-preview {
  border: 2px dashed #556ee6;
  border-radius: 8px;
  padding: 4px;
  background: #f8f9ff;
  animation: live-preview-fade-in 0.2s ease;
}
@keyframes live-preview-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Skeleton field (type selection step) */
.ifm-skeleton-field {
  padding: 8px 2px;
}
.skeleton-label {
  width: 100px;
  height: 12px;
  background: #e5e7eb;
  border-radius: 4px;
  margin-bottom: 8px;
}
.skeleton-input {
  width: 100%;
  height: 38px;
  background: #f3f4f6;
  border-radius: 6px;
  border: 1px dashed #d0d5dd;
}

/* ---- Sidebar ---- */
.ifm-sidebar {
  width: 360px;
  flex-shrink: 0;
  border-left: 1px solid #e9ecef;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sidebar-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}
.btn-back-sidebar {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
  flex-shrink: 0;
}
.btn-back-sidebar:hover {
  border-color: #556ee6;
  color: #556ee6;
}
.sidebar-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
  flex: 1;
}
.sidebar-title i {
  font-size: 18px;
  color: #556ee6;
}
.btn-close-sidebar {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #8c8ca1;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.btn-close-sidebar:hover {
  background: #e9ecef;
  color: #1a1a2e;
}
.sidebar-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 18px;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}
.sidebar-body::-webkit-scrollbar {
  width: 6px;
}
.sidebar-body::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar-body::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}
.sidebar-body::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

/* Sidebar: type selection */
.sidebar-intro {
  font-size: 13px;
  color: #8c8ca1;
  margin-bottom: 12px;
}
.sidebar-type-list {
  display: flex;
  flex-direction: column;
}
.sidebar-type-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.sidebar-type-item:hover {
  background: #f8f9fa;
}
.sidebar-type-item:hover .sidebar-type-icon {
  background: #dde2f9;
}
.sidebar-type-item:hover > i.bx-chevron-right {
  color: #556ee6;
}
.sidebar-type-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #eff1fb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #556ee6;
  font-size: 18px;
  flex-shrink: 0;
}
.sidebar-type-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.sidebar-type-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
}
.sidebar-type-desc {
  font-size: 11px;
  color: #8c8ca1;
}
.sidebar-type-item > i.bx-chevron-right {
  color: #ced4da;
  font-size: 18px;
}

/* Settings Sections */
.settings-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f3f4f6;
}
.settings-section:last-child {
  border-bottom: none;
}
.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
}
.sidebar-config .toggle-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  border-radius: 0;
  padding: 0;
  gap: 0;
}
.sidebar-config .toggle-group label {
  margin-bottom: 0;
}

/* Sidebar: configure form */
.sidebar-config {
  display: flex;
  flex-direction: column;
}
.sidebar-config .form-group {
  margin-bottom: 16px;
}
.sidebar-config .form-group:last-child {
  margin-bottom: 0;
}
.sidebar-config label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 6px;
}
.sidebar-config .form-control {
  font-size: 13px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}
.sidebar-config .form-control:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

/* Options List */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.option-item {
  display: flex;
  gap: 8px;
  align-items: center;
}
.option-item .form-control {
  flex: 1;
}
.btn-remove-option {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
}
.btn-remove-option:hover {
  background: #fecaca;
}
.btn-add-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  border: 1px dashed #d0d5dd;
  background: transparent;
  border-radius: 6px;
  font-size: 12px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-add-option:hover {
  border-color: #556ee6;
  color: #556ee6;
}

/* Config Actions */
.config-actions {
  display: flex;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
  margin-top: 16px;
}
.btn-cancel {
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-cancel:hover {
  border-color: #d0d5dd;
  background: #f8f9fa;
}
.btn-create {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-create:hover:not(:disabled) {
  background: #485ec4;
}
.btn-create:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Sidebar transition */
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: all 0.25s ease;
}
.sidebar-slide-enter,
.sidebar-slide-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* New tab */
.ifm-btn-new-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: 2px dashed #d1d5db;
  background: transparent;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
}
.ifm-btn-new-tab:hover {
  border-color: #10b981;
  color: #10b981;
  background: #f0fdf4;
}
.ifm-new-tab-form {
  margin-top: 8px;
  padding: 16px;
  background: #f0fdf4;
  border: 1px solid #a7f3d0;
  border-radius: 10px;
}
.ifm-new-tab-form .d-flex {
  display: flex;
  align-items: center;
}
.ifm-inline-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #065f46;
  margin-bottom: 14px;
}
.ifm-inline-header i {
  font-size: 16px;
}
</style>
